import { useState, useEffect, useMemo } from 'react';
import { Box, Card, Stack, Typography } from '@mui/material';
import { useAuth } from '../../AuthContext';
import { useNavigate } from 'react-router-dom';
import { tokens } from '../../theme';
import { useTheme } from '@mui/material';
import { toast } from 'react-toastify';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import sleeping from '../../assets/icons/sleeping.png';
import { ResponsiveLine } from '@nivo/line';
import { ResponsivePie } from '@nivo/pie';
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import LaptopMacOutlinedIcon from '@mui/icons-material/LaptopMacOutlined';

import { buildUi } from '../global/uiTokens';
import {
  DetailRow,
  EmptyState,
  Loading,
  RecordCard,
  SectionCard,
  StatCard,
  STATUS_TEXT,
  statusTone,
} from './userShared';

const Home = ({ userId }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === 'dark';
  const ui = useMemo(() => buildUi(colors, isDark), [colors, isDark]);
  const { getData } = useAuth();
  const navigate = useNavigate();
  const [leave, setLeave] = useState([]);
  const [borrow, setBorrow] = useState([]);
  const [laptops, setLaptops] = useState([]);
  const [loading, setLoading] = useState(true);
  // Task deadlines, shown on the calendar as reminders.
  const [tasks, setTasks] = useState([]);
  const [taskMembers, setTaskMembers] = useState([]);


  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const [
          laptopResponse,
          borrowResponse,
          leaveResponse,
          tasksResponse,
          taskMembersResponse,
        ] = await Promise.all([
          getData('proccess.php', 'inventory'),
          getData('proccess.php', 'borrow_V2', userId),
          getData('proccess.php', 'leaves_V2', userId),
          getData('proccess.php', 'tasks_V2'),
          getData('proccess.php', 'task_members_V2'),
        ]);

        if (laptopResponse.success) {
          setLaptops(laptopResponse.data);
        }

        if (borrowResponse.success) {
          setBorrow(borrowResponse.data);
        }

        if (leaveResponse.success) {
          setLeave(leaveResponse.data);
        }

        /**
         * The task tables will not exist on an installation that has not run
         * the task management SQL. A failure there must not take the whole
         * overview down with it, so it is tolerated quietly.
         */
        setTasks(
          tasksResponse?.success && Array.isArray(tasksResponse.data)
            ? tasksResponse.data
            : []
        );
        setTaskMembers(
          taskMembersResponse?.success && Array.isArray(taskMembersResponse.data)
            ? taskMembersResponse.data
            : []
        );
      } catch (err) {
        toast.error('Network error: Failed to fetch data');
        console.error('Fetch error:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [userId, getData]);

  // Transform leave data into calendar events
  const leaveEvents = useMemo(
    () =>
      leave.map((l) => ({
        title: `${l.leave_type} Leave (${STATUS_TEXT[l.status]})`,
        start: l.startDate,
        end: l.endDate,
        backgroundColor: statusTone(colors, l.status),
        borderColor: statusTone(colors, l.status),
      })),
    [leave, colors]
  );

  /**
   * Your task deadlines, as all-day reminders beside your leave.
   *
   * Scoped to YOUR work - tasks you are on, raised, or own - because this is
   * your own overview. Completed tasks are left out: a finished deadline is
   * not a reminder. Overdue outranks priority, since being late is the thing
   * worth noticing.
   */
  const taskEvents = useMemo(() => {
    const me = String(userId || '');
    if (!me) return [];

    const mine = new Set(
      taskMembers
        .filter((m) => String(m.staff_id) === me)
        .map((m) => String(m.task_id))
    );

    const today = new Date().toISOString().slice(0, 10);

    const tone = (task, late) => {
      if (late) return colors.red[500];
      return (
        {
          urgent: colors.orange[500],
          high: colors.orange[400] || colors.orange[500],
          normal: colors.newTeal[500],
          low: colors.gray[500],
        }[task.priority] || colors.newTeal[500]
      );
    };

    return tasks
      .filter((t) => {
        if (String(t.status) === 'completed') return false;
        if (!t.deadline) return false;
        return (
          mine.has(String(t.id)) ||
          String(t.created_by) === me ||
          String(t.owner_id) === me
        );
      })
      .map((t) => {
        const deadline = String(t.deadline).slice(0, 10);
        const late = deadline < today;
        const colour = tone(t, late);

        return {
          id: `task-${t.id}`,
          title: `${t.task_code ? `${t.task_code} · ` : ''}${t.title}`,
          start: deadline,
          allDay: true,
          backgroundColor: colour,
          borderColor: colour,
          extendedProps: { taskId: t.id, overdue: late },
        };
      });
  }, [tasks, taskMembers, userId, colors]);

  const calendarEvents = useMemo(
    () => [...leaveEvents, ...taskEvents],
    [leaveEvents, taskEvents]
  );

  // Compute data for charts
  const getChartData = () => {
    // Line Chart: Most Borrowed Item
    const borrowCounts = borrow.reduce((acc, b) => {
      acc[b.equipment] = (acc[b.equipment] || 0) + 1;
      return acc;
    }, {});
    const mostBorrowedId = Object.keys(borrowCounts).reduce((a, b) => 
      borrowCounts[a] > borrowCounts[b] ? a : b, null
    ) || '0';
    const mostBorrowedLaptop = laptops.find(l => l.id === mostBorrowedId);
    const mostBorrowedName = mostBorrowedLaptop ? mostBorrowedLaptop.name : 'Unknown';

    // Group borrows by month for the most borrowed item
    const months = Array.from({ length: 6 }, (_, i) => {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      return date.toISOString().slice(0, 7); // YYYY-MM
    }).reverse();

    const borrowByMonth = months.map(month => {
      return borrow.filter(b => b.equipment === mostBorrowedId && b.date.startsWith(month)).length;
    });

    // Format for Nivo Line Chart
    const lineChartData = [
      {
        id: `Borrows of ${mostBorrowedName}`,
        data: months.map((month, index) => ({
          x: month,
          y: borrowByMonth[index],
        })),
      },
    ];

    // Pie Chart: Borrowed vs Available
    const availableCount = laptops.filter(l => l.status === 0).length;
    const borrowedCount = laptops.filter(l => l.status !== 0).length;

    // Format for Nivo Pie Chart
    const pieChartData = [
      { id: 'Borrowed', label: 'Borrowed', value: borrowedCount, color: colors.red[400] },
      { id: 'Available', label: 'Available', value: availableCount, color: colors.green[400] },
    ];

    return { lineChartData, pieChartData };
  };

  const chartData = getChartData();

  const counts = useMemo(
    () => ({
      pendingBorrow: borrow.filter((b) => Number(b.status) === 0).length,
      pendingLeave: leave.filter((l) => Number(l.status) === 0).length,
      rejectedBorrow: borrow.filter((b) => Number(b.status) === 3).length,
      rejectedLeave: leave.filter((l) => Number(l.status) === 3).length,
    }),
    [borrow, leave]
  );

  return (
    <Box>
      {loading ? (
        <Loading ui={ui} label="Loading your overview…" />
      ) : (
        <>
          {/* These four cards carried a heading and nothing else - no number
              was ever rendered. They now count the records already fetched. */}
          <Box
            width="100%"
            display="flex"
            flexWrap="wrap"
            gap={1.5}
            marginBottom={2.5}
          >
            <Box flex="1 1 180px">
              <StatCard
                ui={ui}
                tone={colors.orange[500]}
                icon={<PendingActionsOutlinedIcon sx={{ fontSize: 18 }} />}
                label="Pending borrowing"
                value={counts.pendingBorrow}
                sub={`${borrow.length} in total`}
              />
            </Box>
            <Box flex="1 1 180px">
              <StatCard
                ui={ui}
                tone={colors.orange[500]}
                icon={<PendingActionsOutlinedIcon sx={{ fontSize: 18 }} />}
                label="Pending leave"
                value={counts.pendingLeave}
                sub={`${leave.length} in total`}
              />
            </Box>
            <Box flex="1 1 180px">
              <StatCard
                ui={ui}
                tone={colors.red[500]}
                icon={<LaptopMacOutlinedIcon sx={{ fontSize: 18 }} />}
                label="Rejected borrowing"
                value={counts.rejectedBorrow}
                sub={counts.rejectedBorrow ? 'Needs another look' : 'Nothing rejected'}
              />
            </Box>
            <Box flex="1 1 180px">
              <StatCard
                ui={ui}
                tone={colors.red[500]}
                icon={<EventBusyOutlinedIcon sx={{ fontSize: 18 }} />}
                label="Rejected leave"
                value={counts.rejectedLeave}
                sub={counts.rejectedLeave ? 'Needs another look' : 'Nothing rejected'}
              />
            </Box>
          </Box>

          <Box display="flex" flexWrap="wrap-reverse" gap={'.5rem'}>
            <Box flex="1" minWidth="350px">
              <Box display="flex" flexWrap="wrap" gap={'.5rem'}>
                <Box flex="1" minWidth="350px">
                  <SectionCard ui={ui} title="My borrowing">
                    {borrow.length > 0 ? (
                      borrow.map((item) => {
                        const laptop = laptops.find((s) => s.id === item.equipment);
                        return (
                          <RecordCard
                            ui={ui}
                            colors={colors}
                            key={item.id}
                            status={item.status}
                            title={
                              laptop ? `${laptop.Type}: ${laptop.name}` : 'Unknown laptop'
                            }
                          >
                            <DetailRow
                              ui={ui}
                              label="Condition"
                              value={laptop ? laptop.product_condition : 'N/A'}
                            />
                            <DetailRow ui={ui} label="From" value={item.date} />
                            <DetailRow ui={ui} label="For" value={item.duration} />
                            <DetailRow ui={ui} label="Type" value={item.type} />
                            <DetailRow ui={ui} label="Destination" value={item.destination} />
                          </RecordCard>
                        );
                      })
                    ) : (
                      <EmptyState
                        ui={ui}
                        icon={<LaptopMacOutlinedIcon />}
                        title="Nothing borrowed"
                        hint="Requests you make will appear here."
                      />
                    )}
                  </SectionCard>
                </Box>

                <Box flex="1" minWidth="350px">
                  <SectionCard
                    ui={ui}
                    title="My leave"
                    hint={leave.length > 1 ? `Most recent of ${leave.length}.` : undefined}
                  >
                    {leave.length > 0 ? (
                      <RecordCard
                        ui={ui}
                        colors={colors}
                        status={leave[0].status}
                        title={
                          <Stack direction="row" spacing={1} alignItems="center">
                            <img src={sleeping} style={{ width: '1.4rem' }} alt="" />
                            <span>{leave[0].leave_type} leave</span>
                          </Stack>
                        }
                      >
                        <DetailRow
                          ui={ui}
                          label="Dates"
                          value={`${leave[0].startDate} → ${leave[0].endDate}`}
                        />
                        <DetailRow
                          ui={ui}
                          label="Back in office"
                          value={leave[0].back_in_office}
                        />
                      </RecordCard>
                    ) : (
                      <EmptyState
                        ui={ui}
                        icon={<EventBusyOutlinedIcon />}
                        title="No leave on record"
                        hint="Requests you make will appear here."
                      />
                    )}
                  </SectionCard>
                </Box>
              </Box>
                {/* Charts Section */}
                <Box mt="2rem" display="flex" flexWrap="wrap" gap="1rem">
                    <Box flex="1" minWidth="350px" maxWidth="600px">
                    <Typography variant="h5" fontWeight="700" mb="1rem">
                        Most Borrowed Item Over Time
                    </Typography>
                    {chartData.lineChartData[0].data.some(point => point.y > 0) ? (
                        <Card elevation={0} sx={{ backgroundColor: ui.surface, border: `1px solid ${ui.border}`, borderRadius: 3, backgroundImage: 'none', padding: '1rem', height: '250px' }}>
                        <ResponsiveLine
                            data={chartData.lineChartData}
                            margin={{ top: 50, right: 50, bottom: 70, left: 60 }}
                            xScale={{ type: 'point' }}
                            yScale={{ type: 'linear', min: 0, max: 'auto', stacked: false, reverse: false }}
                            axisTop={null}
                            axisRight={null}
                            axisBottom={{
                            tickSize: 5,
                            tickPadding: 5,
                            tickRotation: 0,
                            legend: 'Month',
                            legendOffset: 36,
                            legendPosition: 'middle',
                            }}
                            axisLeft={{
                            tickSize: 5,
                            tickPadding: 5,
                            tickRotation: 0,
                            legend: 'Number of Borrows',
                            legendOffset: -40,
                            legendPosition: 'middle',
                            }}
                            colors={[colors.blue[400]]}
                            pointSize={10}
                            pointColor={{ theme: 'background' }}
                            pointBorderWidth={2}
                            pointBorderColor={{ from: 'serieColor' }}
                            pointLabel="y"
                            pointLabelYOffset={-12}
                            useMesh={true}
                            theme={{
                            axis: {
                                legend: { text: { fill: colors.primary[100] } },
                                ticks: { text: { fill: colors.primary[100] } },
                            },
                            legends: { text: { fill: colors.primary[100] } },
                            }}
                            legends={[
                            {
                                anchor: 'top',
                                direction: 'row',
                                justify: false,
                                translateX: 0,
                                translateY: -40,
                                itemsSpacing: 0,
                                itemWidth: 150,
                                itemHeight: 20,
                                itemTextColor: colors.primary[100],
                                symbolSize: 12,
                                symbolShape: 'circle',
                            },
                            ]}
                        />
                        </Card>
                    ) : (
                        <Typography>No borrowing data available for the most borrowed item.</Typography>
                    )}
                    </Box>
                </Box>
            </Box>
            <Box flex={1} minWidth="350px" maxWidth="500px" overflow="auto">
              <Typography variant="h5" fontWeight="700">
                Event Calendar
              </Typography>
              <Box
                sx={{
                  backgroundColor: ui.surface,
                  border: `1px solid ${ui.border}`,
                  padding: '1rem',
                  borderRadius: '12px',
                  marginTop: '1rem',
                }}
              >
                <FullCalendar
                  // A deadline is a date, not an appointment.
                  displayEventTime={false}
                  eventClick={(info) => {
                    const taskId = info.event.extendedProps?.taskId;
                    if (!taskId) return;
                    info.jsEvent.preventDefault();
                    navigate(`/tasks/${taskId}`);
                  }}
                  plugins={[dayGridPlugin, timeGridPlugin]}
                  initialView="dayGridMonth"
                  events={calendarEvents}
                  height="auto"
                  eventDisplay="block"
                  eventTextColor={colors.primary[400]}
                  headerToolbar={{
                    left: 'prev,next',
                    center: 'title',
                    right: 'dayGridMonth,timeGridWeek,timeGridDay',
                  }}
                />
              </Box>

              <Box mt={3}>
              <Typography variant="h5" fontWeight="700" mb="1rem">
                  Borrowed vs Available Items
              </Typography>
              {chartData.pieChartData.some(item => item.value > 0) ? (
                  <Card elevation={0} sx={{ backgroundColor: ui.surface, border: `1px solid ${ui.border}`, borderRadius: 3, backgroundImage: 'none', padding: '1rem', height: '250px' }}>
                  <ResponsivePie
                      data={chartData.pieChartData}
                      margin={{ top: 40, right: 50, bottom: 50, left: 50 }}
                      innerRadius={0.5}
                      padAngle={0.7}
                      cornerRadius={3}
                      colors={chartData.pieChartData.map(item => item.color)}
                      borderWidth={1}
                      borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                      arcLinkLabelsSkipAngle={10}
                      arcLinkLabelsTextColor={colors.primary[100]}
                      arcLinkLabelsThickness={2}
                      arcLinkLabelsColor={{ from: 'color' }}
                      arcLabelsSkipAngle={10}
                      arcLabelsTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
                      theme={{
                      labels: { text: { fill: colors.primary[100] } },
                      legends: { text: { fill: colors.primary[100] } },
                      }}
                      legends={[
                      {
                          anchor: 'top',
                          direction: 'row',
                          justify: false,
                          translateX: 0,
                          translateY: -40,
                          itemsSpacing: 0,
                          itemWidth: 100,
                          itemHeight: 17,
                          itemTextColor: colors.primary[100],
                          symbolSize: 17,
                          symbolShape: 'circle',
                      },
                      ]}
                  />
                  </Card>
              ) : (
                  <Typography>No laptop data available.</Typography>
              )}
              </Box>
            </Box>
          </Box>

          
        </>
      )}
    </Box>
  );
};

export default Home;